import React from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { Modal, TitleValue, Loader, Table } from '@kaleyra/emmy';

import Shape from '../../../../../components/common/atoms/ColorShape';
import { PerformanceType, TimeRangeFilterType } from '../../../../../interface';
import { LatencyColorCodes, CallbackType } from '../../../../../constants';

import HeaderBodyTemplate from '../../../templates/TitleSearchTable';
import { BaseSeeMorePropsType } from '../../../templates/SeeMoreModal';

import Header from '../Header';

import Styles from './index.module.scss';
import { debounce } from 'lodash';
import { numberLanguageFormat } from '../../../../../utils';

export type MapStateType = {
	data: PerformanceType[];
	tableData: Array<Record<string, any>>;
	filters: TimeRangeFilterType;
	inProgress: boolean;
	page: number;
	total: number;
};

export type DispatchPropsType = {
	onLoad: (filters: TimeRangeFilterType) => void;
	onFilterChange: (filters: TimeRangeFilterType) => void;
	onSearchStrChange: (searchStr?: string) => void;
	onPaginate: () => void;
};

const PerformanceSeeMore: React.FC<
	BaseSeeMorePropsType & MapStateType & DispatchPropsType
> = ({
	data,
	tableData,
	visible,
	filters,
	inProgress,
	total,
	page,
	onPaginate,
	onLoad,
	onFilterChange,
	onSearchStrChange,
	onClose,
}) => {
		const { t } = useTranslation();

		React.useEffect(() => {
			onLoad(filters);
		}, []);

		const debouncedChange = React.useCallback<CallbackType>(
			debounce((value: any = "") => onSearch(value), 400),
			[]
		);

		const onSearch = (searchStr = '') => {
			onSearchStrChange(searchStr);
		};

		const getTableData = () => {
			return tableData.map((item) => {
				const [key] = Object.keys(item);
				return { ...item[key], sender: key };
			});
		};

		const getColor = (value: number): string => {
			if (value >= 80) {
				return LatencyColorCodes.first;
			} else if (value >= 75) {
				return LatencyColorCodes.second;
			} else if (value >= 70) {
				return LatencyColorCodes.third;
			} else {
				return LatencyColorCodes.fourth;
			}
		};

		const getModalBody = () => {
			const { overall = [] } = data[0];
			const [delivered, sent, avgDeliveryTime] = overall;
			const { timerange } = filters;
			const { start_time: start, end_time: end } = timerange || {};

			return (
				<div className={Styles.container}>
					<div className={Styles.detailTilesContainer}>
						{inProgress ? (
							<Loader marginTop={40} />
						) : (
							<>
								<TitleValue
									label={t("KLR02585")}
									labelPosition={'top'}
								>
									<h2>{sent?.total || 0}</h2>
								</TitleValue>
								<TitleValue
									label={t("performanceWidgetTTlDelivered")}
									labelPosition={'top'}
								>
									<h2>{delivered?.total || 0}</h2>
								</TitleValue>
								<TitleValue
									label={t('performanceWidgetAvgDeliveryTime')}
									labelPosition={'top'}
								>
									<h2>{avgDeliveryTime?.total || 0}</h2>
								</TitleValue>
							</>
						)}
					</div>
					<HeaderBodyTemplate
						title={`SMS Latency Details - ${moment.unix(+start).format(
							'DD MMM YYYY'
						)} to ${moment.unix(+end).format('DD MMM YYYY')}`}
						onChange={debouncedChange}
					>
						{inProgress ? (
							<Loader marginTop={70} />
						) : (<>
							<Table
								data={getTableData()}
								columns={[
									{
										header: 'Sender ID',
										accessor: 'sender',
										Cell: ({ value, original }) => (
											<div className={Styles.firstCol}>
												<Shape
													color={getColor(original['0-5'])}
													shapeStyle="square"
												/>
												<p>{value}</p>
											</div>
										)
									},
									{
										header: '00-05s',
										accessor: '00-05 SEC %',
										alignHeader: 'right',
										Cell: ({ value }) => `${numberLanguageFormat(value)}%`
									},
									{
										header: '06-10s',
										accessor: '06-10 SEC %',
										alignHeader: 'right',
										Cell: ({ value }) => `${numberLanguageFormat(value)}%`
									},
									{
										header: '11-20s',
										accessor: '11-20 SEC %',
										alignHeader: 'right',
										Cell: ({ value }) => `${numberLanguageFormat(value)}%`
									},
									{
										header: '21-30s',
										accessor: '21-30 SEC %',
										alignHeader: 'right',
										Cell: ({ value }) => `${numberLanguageFormat(value)}%`
									},
									{
										header: '31-40s',
										accessor: '31-40 SEC %',
										alignHeader: 'right',
										Cell: ({ value }) => `${numberLanguageFormat(value)}%`
									},
									{
										header: '41-50s',
										accessor: '41-50 SEC %',
										alignHeader: 'right',
										Cell: ({ value }) => `${numberLanguageFormat(value)}%`
									},
									{
										header: '51-60s',
										accessor: '51-60 SEC %',
										alignHeader: 'right',
										Cell: ({ value }) => `${numberLanguageFormat(value)}%`
									},
									{
										header: 'More Than 60',
										accessor: 'MORE THAN 60 SEC %',
										alignHeader: 'right',
										Cell: ({ value }) => `${numberLanguageFormat(value)}%`
									},
									{
										header: t("performanceWidgetTTlDelivered"),
										accessor: 'TOTAL DELIVERED COUNT',
										alignHeader: 'right',
										Cell: ({ value }) => value
									}
								]}
								page={page + 1}
								pageSize={25}
								manual
								onPaginate={onPaginate}
								total={total}
								scrollableTarget={'modalContainer'}
								rowClassName={Styles.tableRow}
								labelForNoResult={t('tableNoResults')}
								labelForNoResultDesc={t('tableNoResultsDesc')}
							/></>
						)}
					</HeaderBodyTemplate>
				</div>
			);
		};

		const getModalTitle = () => (
			<div className={Styles.titleContainer}>
				<Header
					filters={filters}
					onFilterChange={onFilterChange}
					showDownload
				/>
			</div>
		);

		return (
			<>
				<Modal
					visible={visible}
					type="fullscreen"
					onClose={onClose}
					title={getModalTitle()}
					body={data.length > 0 ? getModalBody() : <Loader marginTop={100} />}
				/>
			</>
		);
	};
export default PerformanceSeeMore;
